import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const API_BASE = '/api';

const DEFAULTS = {
  logo: null,
  favicon: null,
  primaryColor: null,
  secondaryColor: null,
  loginBackground: null,
  companyName: 'CallEssential',
  hidePoweredBy: false,
  loaded: false,
  loadBranding: (_tenantId: any) => {},
};

const BrandingContext = createContext(DEFAULTS);

export function useBranding() {
  return useContext(BrandingContext);
}

function applyBrandingCssVars(branding) {
  const root = document.documentElement;
  if (branding.primaryColor) {
    root.style.setProperty('--tenant-primary', branding.primaryColor);
  } else {
    root.style.removeProperty('--tenant-primary');
  }
  if (branding.secondaryColor) {
    root.style.setProperty('--tenant-secondary', branding.secondaryColor);
  } else {
    root.style.removeProperty('--tenant-secondary');
  }
}

function applyFavicon(url) {
  if (!url) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

async function fetchBranding() {
  try {
    // Priority 1: ?tenant= URL param (set by subdomain redirect)
    const params = new URLSearchParams(window.location.search);
    const tenantParam = params.get('tenant');

    let url;
    if (tenantParam) {
      url = `${API_BASE}/public/branding?subdomain=${encodeURIComponent(tenantParam)}`;
    } else {
      // Priority 2: detect by full hostname (custom domains)
      const hostname = window.location.hostname;
      url = `${API_BASE}/public/branding?domain=${encodeURIComponent(hostname)}`;
    }

    const res = await fetch(url, { cache: 'default' });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.success || !data.branding) return null;
    return data.branding;
  } catch (_) {
    return null;
  }
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const loadBranding = useCallback(async (tenantId) => {
    try {
      let url;
      if (tenantId) {
        url = `${API_BASE}/public/branding?tenant_id=${tenantId}`;
      } else {
        const hostname = window.location.hostname;
        url = `${API_BASE}/public/branding?domain=${encodeURIComponent(hostname)}`;
      }
      const res = await fetch(url, { cache: 'default' });
      if (!res.ok) return;
      const data = await res.json();
      if (!data.success || !data.branding) return;
      applyFromRaw(data.branding);
    } catch (_) {}
  }, []);

  function applyFromRaw(raw) {
    const mapped = {
      logo: raw.logo_url || null,
      favicon: raw.favicon_url || null,
      primaryColor: raw.primary_color || null,
      secondaryColor: raw.secondary_color || null,
      loginBackground: raw.login_bg_url || null,
      companyName: raw.company_name || 'CallEssential',
      hidePoweredBy: !!raw.hide_powered_by,
    };
    setBranding(mapped);
    applyBrandingCssVars(mapped);
    applyFavicon(mapped.favicon);
  }

  useEffect(() => {
    // When a user is already logged in, their tenant's branding is authoritative
    // and is applied via loadBranding(tenant_id) from App. Skip the domain-based
    // lookup here so it can't clobber the tenant branding — domains without a
    // tenant mapping (e.g. tecducc.synergytalk.org) resolve to the CallEssential
    // default, which previously overwrote the logged-in tenant's branding.
    if (localStorage.getItem('token')) { setLoaded(true); return; }
    fetchBranding()
      .then(raw => { if (raw) applyFromRaw(raw); })
      .finally(() => setLoaded(true));
  }, []);

  return (
    <BrandingContext.Provider value={{ ...branding, loaded, loadBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}
